import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import NavigationBar from "components/Navigation";
import "utils/fontAwesome";
import { memberService } from "services/memberService";
import { facilityService } from "services/facilityService";
import { Building, Theme, BuildingFacility } from "types";
import {
  ReservationContent,
  ReservationHeader,
  HeaderLeft,
  BackButton,
  ReservationTitle,
  ReservationSearch,
  CategoryContainer,
  LeftCategory,
  RightCategory,
  CategoryItem,
  FacilityItem,
  EmptyState
} from "./Reservation.styles";

interface SimpleFacility {
  id: number;
  name: string;
}

interface SubCategory {
  name: string;
  id?: number; // buildingId or themeId
  facilities: SimpleFacility[];
}

interface MainCategory {
  name: string;
  subCategories: SubCategory[];
}

const Reservation: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1); // 1: 메인/서브 카테고리, 2: 서브/시설
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [selectedMainCategoryName, setSelectedMainCategoryName] = useState<string>("건물별");
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const navigate = useNavigate();

  // Fetch buildings and themes from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [buildingsData, themesData] = await Promise.all([
          memberService.getMyBuildings(),
          memberService.getMyThemes()
        ]);
        setBuildings(buildingsData);
        setThemes(themesData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Create category data dynamically
  const categoryData: MainCategory[] = [
    {
      name: "건물별",
      subCategories: buildings.map(building => ({
        name: building.name,
        id: building.id,
        facilities: [] // Facilities will be loaded separately when needed
      }))
    },
    {
      name: "테마별",
      subCategories: themes.map(theme => ({
        name: theme.name,
        id: theme.id,
        facilities: [] // Facilities will be loaded separately when needed
      }))
    }
  ];

  const selectedMainCategory = categoryData.find(cat => cat.name === selectedMainCategoryName) || categoryData[0];

  const handleMainCategoryClick = (category: MainCategory) => {
    setSelectedMainCategoryName(category.name);
  };

  const handleSubCategoryClick = async (subCategory: SubCategory) => {
    // 건물별 또는 테마별 카테고리인 경우 시설 목록을 불러옴
    if (subCategory.id) {
      try {
        let facilities: BuildingFacility[];

        if (selectedMainCategoryName === "건물별") {
          facilities = await facilityService.getBuildingFacilities(subCategory.id);
        } else if (selectedMainCategoryName === "테마별") {
          facilities = await facilityService.getThemeFacilities(subCategory.id);
        } else {
          facilities = [];
        }

        // 시설 목록을 SimpleFacility 형식으로 변환
        const simpleFacilities: SimpleFacility[] = facilities.map(facility => ({
          id: facility.id,
          name: facility.name
        }));

        // 시설 목록이 포함된 새로운 SubCategory 객체 생성
        setSelectedSubCategory({
          ...subCategory,
          facilities: simpleFacilities
        });
      } catch (error) {
        console.error('Failed to fetch facilities:', error);
        setSelectedSubCategory({
          ...subCategory,
          facilities: []
        });
      }
    } else {
      setSelectedSubCategory(subCategory);
    }
    setStep(2);
  };

  const handleFacilityClick = (facilityId: number) => {
    navigate(`/facilities/${facilityId}`, { state: { id: facilityId } });
  };

  const handleBackClick = () => {
    if (step === 2) {
      setStep(1);
      setSelectedSubCategory(null);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <ReservationContent>
        <ReservationHeader>
          <HeaderLeft>
            <BackButton onClick={handleBackClick}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </BackButton>
            <ReservationTitle>카테고리</ReservationTitle>
          </HeaderLeft>
          <ReservationSearch to="/search">
            <FontAwesomeIcon
              icon="magnifying-glass"
              style={{ fontSize: '1.2em' }}
            />
          </ReservationSearch>
        </ReservationHeader>

        <CategoryContainer>
          {step === 1 ? (
            <>
              {/* Step 1: 왼쪽 - 건물별/테마별, 오른쪽 - 선택된 메인 카테고리의 하위 목록 */}
              <LeftCategory>
                {categoryData.map((mainCategory) => (
                  <CategoryItem
                    key={mainCategory.name}
                    active={selectedMainCategory.name === mainCategory.name}
                    onClick={() => handleMainCategoryClick(mainCategory)}
                  >
                    {mainCategory.name}
                  </CategoryItem>
                ))}
              </LeftCategory>

              <RightCategory>
                {selectedMainCategory.subCategories.map((subCategory) => (
                  <CategoryItem
                    key={subCategory.name}
                    active={false}
                    onClick={() => handleSubCategoryClick(subCategory)}
                  >
                    {subCategory.name}
                  </CategoryItem>
                ))}
              </RightCategory>
            </>
          ) : (
            <>
              {/* Step 2: 왼쪽 - 선택한 메인 카테고리의 하위 목록, 오른쪽 - 선택한 하위의 시설 목록 */}
              <LeftCategory>
                {selectedMainCategory.subCategories.map((subCategory) => (
                  <CategoryItem
                    key={subCategory.name}
                    active={selectedSubCategory?.name === subCategory.name}
                    onClick={() => handleSubCategoryClick(subCategory)}
                  >
                    {subCategory.name}
                  </CategoryItem>
                ))}
              </LeftCategory>

              <RightCategory>
                {selectedSubCategory && selectedSubCategory.facilities.length > 0 ? (
                  selectedSubCategory.facilities.map((facility) => (
                    <FacilityItem
                      key={facility.id}
                      onClick={() => handleFacilityClick(facility.id)}
                    >
                      {facility.name}
                    </FacilityItem>
                  ))
                ) : (
                  <EmptyState>시설이 없습니다.</EmptyState>
                )}
              </RightCategory>
            </>
          )}
        </CategoryContainer>
      </ReservationContent>
      <NavigationBar />
    </>
  );
};

export default Reservation;
